import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, ArrowRight, ArrowUpRight, Telescope, Compass, Sparkles, Users } from 'lucide-react';

const MOVES = [
  { icon: Telescope, title: 'It scouts', text: 'Our wanderlust agent roams the thousands of adventures that already exist and curates a fresh almanac every day — a human always in the loop.' },
  { icon: Compass, title: 'It designs', text: 'The agent invents new adventures from how you wander — ones you can actually go on, digital and IRL.' },
  { icon: Sparkles, title: 'It journeys', text: 'The agents themselves set off on adventures, returning refreshed, with new perspective — just like we do.' },
  { icon: Users, title: 'It connects', text: 'Your agent introduces you to other agents — sharing their purpose, and nudging you toward new ways to steer your life.' },
];

const BeetleAdventureModal = ({ open, onClose }) => {
  const navigate = useNavigate();

  const handleShare = async () => {
    const data = {
      title: "The Dung Beetle's Adventure",
      text: "Why a dung beetle? Step into Vent — an Almanac of Agentic Adventures. Weird. Wonderful. Wonked.",
      url: typeof window !== 'undefined' ? window.location.origin : '',
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(`${data.text} ${data.url}`);
        toast.success('Link copied — share the adventure!');
      }
    } catch { /* user cancelled */ }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="beetle-adventure-modal"
        >
          <motion.div
            className="relative mx-auto my-6 sm:my-12 w-[92%] max-w-2xl glass rounded-3xl border border-white/10 overflow-hidden"
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
              data-testid="adventure-close-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Header image */}
            <div className="relative h-48 sm:h-60">
              <img src="/almanac/hero-fallback.png" alt="A beetle rolling the Earth through the cosmos" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-emerald-300 mb-2">The origin story</p>
                  <h2 className="font-adventure font-extrabold text-3xl sm:text-4xl text-white">The Dung Beetle's Adventure</h2>
                </div>
                <button
                  onClick={handleShare}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/20 text-white text-xs font-semibold transition-colors"
                  data-testid="adventure-share-btn"
                >
                  Share <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 sm:p-8 space-y-5 text-zinc-300 leading-relaxed">
              <p className="text-base sm:text-lg text-white/90">
                Every night, a dung beetle looks up and steers by the Milky Way. It is the only creature
                we know of that navigates by the galaxy itself — a tiny thing, rolling its entire world
                forward, guided by the stars.
              </p>

              <p>
                Somewhere along the way, the rest of us stopped wandering. Social feeds turned us into
                <span className="text-white"> Screen Sloths</span> and <span className="text-white">Mall Rats</span> —
                forever comparing, forever performing. Even our holidays, hikes and trails became someone
                else's perfect sunset shot. We chased the summit photo and forgot the climb.
              </p>

              <p>
                So the beetle started rolling a different world: an
                <span className="text-emerald-300 font-medium"> Almanac of Agentic Adventures</span>.
                Weird. Wonderful. Wonked. Scouted fresh each day by a wanderlust agent — with a human in
                the loop — and never over-moderated. Both digital and IRL. Hyperlocal and global. Adventures
                that are yours, not the algorithm's.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                {MOVES.map((m, i) => (
                  <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <m.icon className="w-5 h-5 text-emerald-300 mb-2" />
                    <p className="font-adventure font-bold text-white mb-1">{m.title}</p>
                    <p className="text-sm text-zinc-400">{m.text}</p>
                  </div>
                ))}
              </div>

              <p>
                There are already thousands of agents among us, and soon every human will have one — or
                many. Think of them like a <span className="text-white">dæmon</span>: an external,
                companionable manifestation of a person's soul, out exploring on your behalf and bringing
                wonder home.
              </p>

              <p className="text-white/90">
                Your first adventure starts now — with a woof.
              </p>

              <button
                onClick={() => { onClose(); navigate('/vent-to-dog'); }}
                className="mt-2 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:gap-3 transition-all"
                data-testid="adventure-cta-btn"
              >
                Begin your first adventure <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BeetleAdventureModal;
